import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import React from 'react'
import CHRButton from '../button'
import {
  DESCRIPTION_HINT,
  FORM_FIELD_IDS,
  FORM_LABELS,
  TITLE_HINT,
} from './constants'
import { useCreateEditPageModalStyles } from './styles'
import { useCreateEditPageForm } from './useCreateEditPageForm'
import { CreateEditPageModalProps } from './types'

export default function CreateEditPageModal({
  folderId,
  initialTitle = '',
  itemCount,
  open,
  onClose,
  onCreated,
}: CreateEditPageModalProps) {
  const classes = useCreateEditPageModalStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const {
    title,
    description,
    titleError,
    formError,
    isSubmitting,
    hasTitleError,
    canSubmit,
    handleClose,
    handleTitleChange,
    handleTitleBlur,
    handleDescriptionChange,
    handleSubmit,
  } = useCreateEditPageForm({
    folderId,
    initialTitle,
    itemCount,
    open,
    onClose,
    onCreated,
  })

  const titleHelperId = hasTitleError
    ? FORM_FIELD_IDS.titleError
    : FORM_FIELD_IDS.titleHint
  const itemLabel = itemCount === 1 ? 'item' : 'items'

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={FORM_FIELD_IDS.modalTitle}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography
            className={classes.dialogHeading}
            id={FORM_FIELD_IDS.modalTitle}
            component="h2"
          >
            create edit page
          </Typography>
          <IconButton
            size="small"
            aria-label="close create edit page modal"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.introText} component="p">
          create an edit page from all {itemCount} {itemLabel} in this folder
        </Typography>

        <Box className={classes.formStack} component="form" noValidate>
          <Box className={classes.fieldWrap}>
            <TextField
              id={FORM_FIELD_IDS.title}
              name={FORM_FIELD_IDS.title}
              className={classes.formField}
              label={FORM_LABELS.title}
              value={title}
              onChange={event => handleTitleChange(event.target.value)}
              onBlur={handleTitleBlur}
              fullWidth
              required
              disabled={isSubmitting}
              error={hasTitleError}
              helperText={hasTitleError ? titleError : TITLE_HINT}
              FormHelperTextProps={{
                id: titleHelperId,
                role: hasTitleError ? 'alert' : undefined,
                'aria-live': hasTitleError ? 'polite' : undefined,
              }}
              InputLabelProps={{
                shrink: true,
                htmlFor: FORM_FIELD_IDS.title,
              }}
              inputProps={{
                'aria-required': true,
                'aria-invalid': hasTitleError,
                'aria-describedby': titleHelperId,
              }}
            />
          </Box>

          <Box className={classes.fieldWrap}>
            <TextField
              id={FORM_FIELD_IDS.description}
              name={FORM_FIELD_IDS.description}
              className={classes.formField}
              label={FORM_LABELS.description}
              value={description}
              onChange={event => handleDescriptionChange(event.target.value)}
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              disabled={isSubmitting}
              helperText={DESCRIPTION_HINT}
              FormHelperTextProps={{
                id: FORM_FIELD_IDS.descriptionHint,
              }}
              InputLabelProps={{
                shrink: true,
                htmlFor: FORM_FIELD_IDS.description,
              }}
              inputProps={{
                'aria-describedby': FORM_FIELD_IDS.descriptionHint,
              }}
            />
          </Box>
        </Box>

        {formError ? (
          <Typography
            id={FORM_FIELD_IDS.formError}
            className={classes.errorMessage}
            role="alert"
            aria-live="polite"
            component="p"
          >
            {formError}
          </Typography>
        ) : null}

        <Box className={classes.actions}>
          <CHRButton
            label="create edit page"
            onClick={handleSubmit}
            hoverEffects={true}
            isSubmitting={isSubmitting}
            disabled={!canSubmit}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
